import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Download, Eye, EyeOff } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { Checkbox } from '../components/atoms/Checkbox';
import { EmptyState } from '../components/atoms/EmptyState';
import { Inline } from '../components/atoms/Inline/Inline';
import { Input } from '../components/atoms/Input/Input';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Select } from '../components/atoms/Select/Select';
import { Stack } from '../components/atoms/Stack/Stack';
import { Text } from '../components/atoms/Typography/Text';
import { Textarea } from '../components/atoms/Textarea/Textarea';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { ParMapLegend, ParMapPhoto } from '../components/lighting/ParMapPhoto';
import { PlacementEditor, type EditTarget, type EditorMode } from '../components/lighting/PlacementEditor';
import { CoralPlacementList } from '../components/lighting/CoralPlacementList';
import { LightEvaluationCard } from '../components/lighting/LightEvaluationCard';
import { listLivestock } from '../services/livestockService';
import {
  addLightCoral,
  addLightPoint,
  deleteLightCoral,
  deleteLightPoint,
  deleteLightSurvey,
  downloadOverlayImage,
  formatSurveyDate,
  getLightSurvey,
  lightSurveysKey,
  pointLabel,
  saveBlob,
  updateLightCoral,
  updateLightPoint,
  updateLightSurvey,
  type LightGroup,
  type LightSurveyDetail,
} from '../services/lightSurveyService';
import { invalidateSharedCorals } from '../lib/sharedCorals';
import '../components/lighting/Lighting.css';

function optionalNumber(value: string): number | null | undefined {
  if (value.trim() === '') return null;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/** Name, sensor, intensity, hours and notes of a survey; delete it. */
function SurveyDetailsForm({ survey, onSaved, onDelete }: { survey: LightSurveyDetail; onSaved: (s: LightSurveyDetail) => void; onDelete: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(survey.title);
  const [sensor, setSensor] = useState(survey.sensor ?? '');
  const [intensity, setIntensity] = useState(survey.intensityPercent === null ? '' : String(survey.intensityPercent));
  const [hours, setHours] = useState(survey.photoperiodHours === null ? '' : String(survey.photoperiodHours));
  const [notes, setNotes] = useState(survey.notes ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      updateLightSurvey(survey.aquariumId, survey.id, {
        title: title.trim(),
        sensor: sensor.trim() || null,
        intensity_percent: optionalNumber(intensity) ?? null,
        photoperiod_hours: optionalNumber(hours) ?? null,
        notes: notes.trim() || null,
      }),
    onSuccess: onSaved,
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (title.trim() === '') next.title = String(t('lighting.new.titleRequired'));
    const i = optionalNumber(intensity);
    if (i === undefined || (i !== null && (!Number.isInteger(i) || i < 1 || i > 100))) next.intensity = String(t('lighting.new.intensityInvalid'));
    const h = optionalNumber(hours);
    if (h === undefined || (h !== null && (h <= 0 || h > 24))) next.hours = String(t('lighting.new.hoursInvalid'));
    setErrors(next);
    if (Object.keys(next).length === 0) save.mutate();
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="4">
        <Input id="survey-title" label={t('lighting.new.title')} value={title} onChange={(e) => setTitle(e.target.value)} error={errors.title} maxLength={120} fullWidth />
        <Input id="survey-sensor" label={t('lighting.new.sensor')} value={sensor} onChange={(e) => setSensor(e.target.value)} maxLength={80} fullWidth />
        <Input id="survey-intensity" type="number" inputMode="numeric" min={1} max={100} label={t('lighting.new.intensity')} helperText={t('lighting.new.intensityHint')} value={intensity} onChange={(e) => setIntensity(e.target.value)} error={errors.intensity} fullWidth />
        <Input id="survey-hours" type="number" inputMode="decimal" min={0.5} max={24} step={0.5} label={t('lighting.new.hours')} helperText={t('lighting.new.hoursHint')} value={hours} onChange={(e) => setHours(e.target.value)} error={errors.hours} fullWidth />
        <Textarea id="survey-notes" label={t('lighting.details.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
        {save.isError && <Alert compact>{t('lighting.details.error')}</Alert>}
        {save.isSuccess && <Alert tone="success" live="polite" compact>{t('lighting.details.saved')}</Alert>}
        <Inline gap="2" justify="between">
          <Button type="submit" variant="secondary" isLoading={save.isPending}>{t('lighting.details.save')}</Button>
          <Button type="button" variant="danger" onClick={onDelete}>{t('lighting.details.delete')}</Button>
        </Inline>
      </Stack>
    </form>
  );
}

/**
 * One PAR survey ("Lichtkarte"): the photo with the measured points, the
 * estimated area and the placed corals; adding points and corals; the placement
 * check per coral; "what if the lamp ran at X %"; and where a new coral of a
 * group would fit. Every write returns the recomputed survey.
 */
export function LightSurveyPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { aquariumId = '', surveyId = '' } = useParams<{ aquariumId: string; surveyId: string }>();
  const [mode, setMode] = useState<EditorMode>('point');
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  // Off = the plain photo, e.g. to look at the corals without the tags.
  const [showMeasurements, setShowMeasurements] = useState(true);
  const [simInput, setSimInput] = useState('');
  const [simulated, setSimulated] = useState<number | undefined>(undefined);
  const [finderGroup, setFinderGroup] = useState<LightGroup | ''>('');
  const [announcement, setAnnouncement] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Bumped after every successful save: a fresh, empty form (kept as typed on a failure).
  const [saved, setSaved] = useState(0);

  const key = ['light-surveys', aquariumId, surveyId] as const;
  const query = useQuery({
    queryKey: [...key, simulated ?? null],
    queryFn: () => getLightSurvey(aquariumId, surveyId, simulated),
    enabled: Boolean(aquariumId && surveyId),
    placeholderData: (previous) => previous,
  });
  const livestock = useQuery({
    queryKey: ['livestock', aquariumId, 'light-corals'],
    queryFn: () => listLivestock(aquariumId, { category: 'coral' }),
    enabled: Boolean(aquariumId),
  });

  const applied = (survey: LightSurveyDetail, message: string): void => {
    queryClient.setQueryData([...key, null], survey);
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: lightSurveysKey(aquariumId), exact: true });
    setAnnouncement(message);
  };

  const savePoint = useMutation({
    mutationFn: ({ input, id }: { input: Parameters<typeof addLightPoint>[2]; id: string | null }) =>
      id ? updateLightPoint(aquariumId, surveyId, id, input) : addLightPoint(aquariumId, surveyId, input),
    onSuccess: (survey, { id }) => {
      applied(survey, String(t(id ? 'lighting.announce.pointSaved' : 'lighting.announce.pointAdded')));
      setEditing(null);
      setPosition(null);
      setSaved((n) => n + 1);
    },
  });
  const saveCoral = useMutation({
    mutationFn: ({ input, id }: { input: Parameters<typeof addLightCoral>[2]; id: string | null }) =>
      id ? updateLightCoral(aquariumId, surveyId, id, input) : addLightCoral(aquariumId, surveyId, input),
    onSuccess: (survey, { id }) => {
      applied(survey, String(t(id ? 'lighting.announce.coralSaved' : 'lighting.announce.coralAdded')));
      invalidateSharedCorals(queryClient, aquariumId);
      setEditing(null);
      setPosition(null);
      setSaved((n) => n + 1);
    },
  });
  const remove = useMutation({
    mutationFn: ({ kind, id }: { kind: 'point' | 'coral'; id: string }) =>
      kind === 'point' ? deleteLightPoint(aquariumId, surveyId, id) : deleteLightCoral(aquariumId, surveyId, id),
    onSuccess: (survey, { kind }) => {
      applied(survey, String(t('lighting.announce.removed')));
      if (kind === 'coral') invalidateSharedCorals(queryClient, aquariumId);
    },
  });
  const exportImage = useMutation({
    mutationFn: (survey: LightSurveyDetail) => downloadOverlayImage(aquariumId, survey, { estimate: showGrid, intensity: simulated }),
    onSuccess: ({ blob, filename }) => {
      saveBlob(blob, filename);
      setAnnouncement(String(t('lighting.map.exported')));
    },
  });
  const removeSurvey = useMutation({
    mutationFn: () => deleteLightSurvey(aquariumId, surveyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: lightSurveysKey(aquariumId) });
      navigate(`/aquariums/${encodeURIComponent(aquariumId)}/lighting`);
    },
  });

  const survey = query.data;
  const assessment = survey?.assessment;
  const shownPar = useMemo(() => new Map((assessment?.points ?? []).map((p) => [p.id, p])), [assessment]);
  const statusOf = useMemo(() => new Map((assessment?.corals ?? []).map((a) => [a.coralId, a.status])), [assessment]);
  const spots = finderGroup ? assessment?.spots.find((s) => s.group === finderGroup) : undefined;
  const highlightIds = spots ? new Set(spots.pointIds) : null;
  const busy = savePoint.isPending || saveCoral.isPending || remove.isPending;
  const backLink = (
    <Link className="lighting__back-link" to={`/aquariums/${encodeURIComponent(aquariumId)}/lighting`}>
      {t('lighting.detail.back')}
    </Link>
  );

  const startEdit = (target: EditTarget): void => {
    setEditing(target);
    setMode(target.kind);
    setPosition({ x: target.x, y: target.y });
  };

  let content;
  if (query.isPending) {
    content = <LiveRegion busy><Text color="muted">{t('lighting.loading')}</Text></LiveRegion>;
  } else if (query.isError || !survey || !assessment) {
    content = <LoadErrorState title={t('lighting.detail.loadError')} onRetry={() => void query.refetch()} retryLabel={t('lighting.retry')} titleLevel={2} />;
  } else {
    const aspect = survey.imageWidth && survey.imageHeight ? survey.imageWidth / survey.imageHeight : 4 / 3;
    const counts = assessment.corals.reduce<Record<string, number>>((acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }), {});
    content = (
      <>
        <Section title={t('lighting.map.title')} description={t('lighting.map.description')}>
          <Stack gap="4">
            {!survey.imageUrl && <Alert tone="info" live="off" compact>{t('lighting.map.noPhoto')}</Alert>}
            {assessment.simulated && (
              <Alert tone="info" live="polite" compact>{t('lighting.simulate.active', { value: assessment.intensityPercent ?? 0, measured: survey.intensityPercent ?? 0 })}</Alert>
            )}
            <Card>
              <Stack gap="4">
                <div className="par-map__toolbar">
                  <Text color="secondary" size="sm">
                    {t('lighting.map.counts', { points: survey.points.length, corals: survey.corals.length })}
                  </Text>
                  <Inline gap="2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      aria-pressed={showMeasurements}
                      data-testid="toggle-measurements"
                      onClick={() => setShowMeasurements((v) => !v)}
                    >
                      {showMeasurements ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                      {t('lighting.map.showMeasurements')}
                    </Button>
                    {survey.points.length + survey.corals.length > 0 && (
                      <Button type="button" variant="ghost" size="sm" isLoading={exportImage.isPending} onClick={() => exportImage.mutate(survey)}>
                        <Download size={16} aria-hidden="true" />
                        {t('lighting.map.export')}
                      </Button>
                    )}
                  </Inline>
                </div>
                {exportImage.isError && <Alert compact>{t('lighting.map.exportError')}</Alert>}
                <ParMapPhoto
                  imageUrl={survey.imageUrl}
                  aspect={aspect}
                  points={survey.points.map((p) => ({ id: p.id, sequence: p.sequence, x: p.x, y: p.y, par: shownPar.get(p.id)?.par ?? p.par }))}
                  corals={survey.corals.map((c, i) => ({ id: c.id, number: i + 1, x: c.x, y: c.y, status: statusOf.get(c.id) ?? 'no_data' }))}
                  grid={assessment.grid}
                  showGrid={showGrid}
                  showMeasurements={showMeasurements}
                  highlightIds={highlightIds}
                  pending={position}
                  onPick={(x, y) => setPosition({ x, y })}
                />
                <Inline gap="4" justify="between" align="center">
                  <ParMapLegend />
                  <Checkbox label={t('lighting.map.showEstimate')} checked={showGrid} disabled={!showMeasurements} onChange={(e) => setShowGrid(e.target.checked)} />
                </Inline>
                <Text color="muted" variant="caption">{t('lighting.map.estimateNote')}</Text>
              </Stack>
            </Card>
            <Card>
              <PlacementEditor
                key={`${editing?.id ?? 'new'}-${saved}`}
                mode={mode}
                onModeChange={setMode}
                position={position}
                onPositionChange={setPosition}
                editing={editing}
                onCancelEdit={() => setEditing(null)}
                corals={(livestock.data ?? []).map((c) => ({ id: c.id, name: c.commonName }))}
                pending={savePoint.isPending || saveCoral.isPending}
                failed={savePoint.isError || saveCoral.isError}
                onSavePoint={(input, id) => savePoint.mutate({ input, id })}
                onSaveCoral={(input, id) => saveCoral.mutate({ input, id })}
              />
            </Card>
          </Stack>
        </Section>

        <Section title={t('lighting.corals.title')} description={survey.imageId ? `${String(t('lighting.corals.description'))} ${String(t('lighting.corals.shared'))}` : t('lighting.corals.description')}>
          {survey.corals.length === 0 ? (
            <EmptyState
              headline={t('lighting.corals.emptyTitle')}
              description={t('lighting.corals.emptyDescription')}
              actionLabel={t('lighting.corals.emptyAction')}
              actionVariant="secondary"
              onAction={() => {
                setEditing(null);
                setMode('coral');
              }}
              headingLevel={3}
            />
          ) : (
            <Card>
              <Stack gap="3">
                <Text weight="medium" data-testid="coral-summary">
                  {t('lighting.corals.summary', { ok: counts.ok ?? 0, low: counts.too_low ?? 0, high: counts.too_high ?? 0, open: (counts.uncertain ?? 0) + (counts.unknown_group ?? 0) + (counts.no_data ?? 0) })}
                </Text>
                <CoralPlacementList
                  corals={survey.corals}
                  assessments={assessment.corals}
                  points={survey.points}
                  busy={busy}
                  onEdit={(c) => startEdit({ kind: 'coral', id: c.id, x: c.x, y: c.y, zone: c.zone, livestockId: c.livestockId, label: c.label, lightGroup: c.lightGroup })}
                  onRemove={(c) => remove.mutate({ kind: 'coral', id: c.id })}
                />
                <Text color="muted" variant="caption">{t('lighting.corals.source', { source: assessment.source })}</Text>
              </Stack>
            </Card>
          )}
        </Section>

        <Section title={t('lighting.points.title')}>
          {survey.points.length === 0 ? (
            <EmptyState
              headline={t('lighting.points.emptyTitle')}
              description={t('lighting.points.emptyDescription')}
              actionLabel={t('lighting.points.emptyAction')}
              actionVariant="secondary"
              onAction={() => {
                setEditing(null);
                setMode('point');
              }}
              headingLevel={3}
            />
          ) : (
            <Card>
              <ul className="lighting__list" data-testid="light-points">
                {survey.points.map((p) => {
                  const shown = shownPar.get(p.id);
                  const label = pointLabel(p.sequence);
                  return (
                    <li key={p.id} className="lighting__row">
                      <Stack gap="1" className="lighting__row-text">
                        <Text weight="semibold">{t('lighting.points.item', { label, par: shown?.par ?? p.par })}</Text>
                        <Text color="secondary" size="sm">
                          {p.zone ? t(`lighting.zones.${p.zone}`) : t('lighting.zones.unknown')}
                          {` · ${String(t('lighting.points.position', { x: Math.round(p.x * 100), y: Math.round(p.y * 100) }))}`}
                          {shown?.dli != null && ` · ${String(t('lighting.corals.dli', { dli: shown.dli }))}`}
                          {assessment.simulated && ` · ${String(t('lighting.points.measured', { par: p.par }))}`}
                        </Text>
                      </Stack>
                      <Inline gap="2">
                        <Button size="sm" variant="secondary" disabled={busy} onClick={() => startEdit({ kind: 'point', id: p.id, x: p.x, y: p.y, par: p.par, zone: p.zone })} aria-label={t('lighting.points.editLabel', { label })}>
                          {t('lighting.edit')}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate({ kind: 'point', id: p.id })} aria-label={t('lighting.points.removeLabel', { label })}>
                          {t('lighting.remove')}
                        </Button>
                      </Inline>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
          {remove.isError && <Alert compact>{t('lighting.editor.error')}</Alert>}
        </Section>

        <Section title={t('lighting.evaluation.title')} description={t('lighting.evaluation.description')}>
          <LightEvaluationCard aquariumId={aquariumId} surveyId={surveyId} pointCount={survey.points.length} />
        </Section>

        <Section title={t('lighting.finder.title')} description={t('lighting.finder.description')}>
          <Card>
            <Stack gap="3">
              <Select
                id="light-finder-group"
                label={t('lighting.finder.group')}
                value={finderGroup}
                options={[
                  { value: '', label: String(t('lighting.finder.choose')) },
                  ...(['soft', 'lps', 'sps'] as const).map((g) => ({ value: g, label: String(t(`lighting.groups.${g}`)) })),
                ]}
                onChange={(e) => setFinderGroup(e.target.value as LightGroup | '')}
              />
              {spots && (
                <LiveRegion>
                  <Text data-testid="finder-result">
                    {spots.pointIds.length === 0
                      ? t('lighting.finder.none', { min: spots.minPar, max: spots.maxPar })
                      : t('lighting.finder.result', {
                          min: spots.minPar,
                          max: spots.maxPar,
                          points: spots.pointIds
                            .map((id) => {
                              const p = survey.points.find((pt) => pt.id === id);
                              return p ? `${pointLabel(p.sequence)} (${shownPar.get(id)?.par ?? p.par})` : '';
                            })
                            .filter(Boolean)
                            .join(', '),
                        })}
                  </Text>
                </LiveRegion>
              )}
            </Stack>
          </Card>
        </Section>

        <Section title={t('lighting.simulate.title')} description={t('lighting.simulate.description')}>
          <Card>
            {survey.intensityPercent === null ? (
              <Text>{t('lighting.simulate.needIntensity')}</Text>
            ) : (
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  const n = Number(simInput);
                  if (Number.isInteger(n) && n >= 1 && n <= 100) setSimulated(n === survey.intensityPercent ? undefined : n);
                }}
              >
                <Stack gap="3">
                  <Input
                    id="light-simulate"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={100}
                    label={t('lighting.simulate.label', { measured: survey.intensityPercent })}
                    value={simInput}
                    onChange={(e) => setSimInput(e.target.value)}
                    error={simInput !== '' && !(Number.isInteger(Number(simInput)) && Number(simInput) >= 1 && Number(simInput) <= 100) ? String(t('lighting.new.intensityInvalid')) : undefined}
                  />
                  <Inline gap="2">
                    <Button type="submit" variant="secondary" isLoading={query.isFetching && simulated !== undefined}>{t('lighting.simulate.apply')}</Button>
                    {simulated !== undefined && (
                      <Button type="button" variant="ghost" onClick={() => { setSimulated(undefined); setSimInput(''); }}>
                        {t('lighting.simulate.reset')}
                      </Button>
                    )}
                  </Inline>
                  <Text color="muted" variant="caption">{t('lighting.simulate.note')}</Text>
                </Stack>
              </form>
            )}
          </Card>
        </Section>

        <Section title={t('lighting.details.title')}>
          <Card>
            <SurveyDetailsForm key={survey.updatedAt} survey={survey} onSaved={(s) => applied(s, String(t('lighting.details.saved')))} onDelete={() => setConfirmDelete(true)} />
          </Card>
        </Section>

        <Dialog
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={t('lighting.details.confirmTitle')}
          size="sm"
          footer={
            <Inline gap="2" justify="end">
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>{t('lighting.cancel')}</Button>
              <Button variant="danger" isLoading={removeSurvey.isPending} onClick={() => removeSurvey.mutate()}>{t('lighting.details.delete')}</Button>
            </Inline>
          }
        >
          <Stack gap="3">
            <Text>{t('lighting.details.confirmText')}</Text>
            {removeSurvey.isError && <Alert compact>{t('lighting.details.deleteError')}</Alert>}
          </Stack>
        </Dialog>
      </>
    );
  }

  const meta = survey
    ? [
        formatSurveyDate(survey.measuredAt, i18n.language || 'de'),
        survey.sensor,
        survey.intensityPercent !== null ? String(t('lighting.meta.intensity', { value: survey.intensityPercent })) : null,
        survey.photoperiodHours !== null ? String(t('lighting.meta.hours', { value: survey.photoperiodHours })) : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader back={backLink} title={survey?.title ?? t('lighting.detail.title')} description={meta} />
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
          {content}
        </PageContent>
      }
    />
  );
}

export default LightSurveyPage;
